// Iter-58: Image responsive helpers.
// Most guide hero images come from Unsplash, which supports `w=` URL params.
// We generate a `srcset` that lets the browser pick the right resolution
// for the device — saving 30–60% bandwidth on mobile and improving LCP
// (Core Web Vitals).
//
// For non-Unsplash CDN URLs we return null so the caller can fall back
// to a single src.

const UNSPLASH_HOSTS = ["images.unsplash.com", "plus.unsplash.com"];

function isUnsplash(url) {
  if (!url || typeof url !== "string") return false;
  try {
    const u = new URL(url);
    return UNSPLASH_HOSTS.includes(u.hostname);
  } catch {
    return false;
  }
}

/**
 * Build a width-based srcset string for an Unsplash image URL.
 * Returns null when the URL isn't from a supported CDN (caller falls back).
 *
 * @param {string} url - source image URL
 * @param {number[]} widths - width buckets to emit (default: 400, 800, 1200, 1600)
 */
export function buildSrcSet(url, widths = [400, 800, 1200, 1600]) {
  if (!isUnsplash(url)) return null;
  try {
    const u = new URL(url);
    const parts = widths.map((w) => {
      const params = new URLSearchParams(u.search);
      params.set("w", String(w));
      params.set("auto", params.get("auto") || "format");
      params.set("fit", params.get("fit") || "crop");
      params.set("q", params.get("q") || "75");
      return `${u.origin}${u.pathname}?${params.toString()} ${w}w`;
    });
    return parts.join(", ");
  } catch {
    return null;
  }
}

/**
 * Standard `sizes` attribute for guide card images.
 * Mobile: full-width minus 32px gutter; tablet: half-column; desktop: third-column.
 */
export const GUIDE_CARD_SIZES =
  "(max-width: 640px) calc(100vw - 32px), (max-width: 1024px) 50vw, 33vw";

/**
 * Standard sizes for hero-style images (full-width above the fold).
 */
export const HERO_IMAGE_SIZES =
  "(max-width: 640px) 100vw, (max-width: 1024px) 70vw, 1200px";
